import { useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { patchCatalogKitOrderQuantity } from '../../../../../../../../services/modules/catalogProducts/catalogKitQuantityCache.utils';
import useOrderBookStore from '../../../../../../../../stores/OrderBook/orderBook.store';
import useProductDetailsStore from '../../../../../../../../stores/ProductDetails/productDetails.store';
import useOrderBook from '../../../../../../../order-book/hooks/useOrderBook';
import { OrderProductPriceGridV2 } from '../../../../../../../orders/products/product/types/orderProductPriceV2.types';
import { kitActionsRepository } from '../../../../../../repositories/kitActions/kitActions.repository';
import { CombinationButtonStatusEnum, IGridStatus } from '../../../../../hooks/useCombination';
import useProductDetailsActions from '../../../../../hooks/useProductsDetailsActions';
import useHandleUpdateKitOnOrderErrorResponse from '../../../hooks/useHandleUpdateKitErrorResponse';
import { KitDetailsCombinationProps } from '../KitDetailsCombination';

type UseKitDetailsCombinationProps = Pick<
  KitDetailsCombinationProps,
  'combination' | 'pushItem' | 'refetchKitRelations' | 'errorVersion' | 'isOfflineMode'
>;

const useKitDetailsCombination = ({
  combination,
  errorVersion,
  isOfflineMode,
  pushItem,
  refetchKitRelations,
}: UseKitDetailsCombinationProps) => {
  const { openOrderBook } = useOrderBook();
  const queryClient = useQueryClient();
  const kitGridRef = useRef<HTMLInputElement>(null);
  const { handleCloseModal } = useProductDetailsActions();
  const { handleError } = useHandleUpdateKitOnOrderErrorResponse();
  const { selectedOrderId, reloadOrderItemsQuantity } = useOrderBookStore(
    useShallow((state) => ({
      reloadOrderItemsQuantity: state.reloadOrderItemsQuantity,
      selectedOrderId: state.selectedOrder?.id,
    })),
  );
  const { from, setOpenedByOrderBook } = useProductDetailsStore(
    useShallow((state) => ({
      setOpenedByOrderBook: state.setOpenedByOrderBook,
      from: state.from,
    })),
  );

  const [gridButtonStatus, setGridButtonStatus] = useState<IGridStatus>();

  const gridToPrices: OrderProductPriceGridV2[] = [
    {
      orderWholeSalePrice: combination.kitWholesalePrice ?? 0,
      productSizeActive: true,
      productSizeName: 'KIT',
      orderItemQuantity: combination.orderQuantity ?? undefined,
    },
  ];

  const grid = {
    skuId: combination.kitId,
    sizeName: 'KIT',
    available: true,
    availableAmount: R.isNil(combination.kitAvailableAmount)
      ? combination.kitAvailableAmount
      : Number(combination.kitAvailableAmount),
    quantity: combination.orderQuantity,
    sizeActive: true,
    sizeUnique: false,
    sizePosition: 0,
  };

  useEffect(() => {
    const newButtonStatus = combination.orderQuantity
      ? CombinationButtonStatusEnum.ADDED
      : CombinationButtonStatusEnum.ADD;

    if (newButtonStatus !== gridButtonStatus?.type) {
      setGridButtonStatus({
        type: newButtonStatus,
        itemQuantity: combination.orderQuantity ?? 0,
      });
    }

    if (kitGridRef.current?.value !== String(combination.orderQuantity) && !!kitGridRef.current) {
      kitGridRef.current.value = String(combination.orderQuantity);
    }
  }, [errorVersion, combination.orderQuantity]);

  const updateKitGridWithNoOrder = useCallback(() => {
    openOrderBook();
    handleCloseModal();
    setOpenedByOrderBook({ state: true, kitId: combination.kitId, from: from });
  }, [combination.kitId, handleCloseModal, openOrderBook, setOpenedByOrderBook]);

  const handleOnChangeGrid = (value: number) => {
    if (
      value === combination.orderQuantity ||
      (!R.isNil(combination.kitAvailableAmount) && value > combination.kitAvailableAmount)
    ) {
      kitGridRef.current!.value = String(combination.orderQuantity);
      return;
    }

    if (!combination.orderId) {
      updateKitGridWithNoOrder();
      return;
    }

    setGridButtonStatus({
      type: CombinationButtonStatusEnum.ADDED,
      itemQuantity: value,
    });

    pushItem({
      kitId: combination.kitId,
      orderId: combination.orderId,
      quantity: value,
    });
  };

  const handleRemoveKitFromOrder = useCallback(async () => {
    if (!combination.orderId) {
      updateKitGridWithNoOrder();
      return;
    }

    setGridButtonStatus({
      type: CombinationButtonStatusEnum.LOADING,
      itemQuantity: 0,
    });

    const response = await kitActionsRepository(isOfflineMode).unshopKit({
      orderId: combination.orderId,
      kitId: combination.kitId,
    });

    if (!response.ok && response.data) {
      setGridButtonStatus({
        type: CombinationButtonStatusEnum.ADDED,
        itemQuantity: 0,
      });

      const error = response.data;
      handleError(error);
      return;
    }

    refetchKitRelations([
      {
        orderId: combination.orderId,
        kitId: combination.kitId,
        quantity: 0,
      },
    ]);

    patchCatalogKitOrderQuantity({
      queryClient,
      orderId: combination.orderId,
      kitId: combination.kitId,
      orderQuantity: 0,
    });
    setGridButtonStatus({
      type: CombinationButtonStatusEnum.ADD,
      itemQuantity: 0,
    });
    const isSelectedOrder = combination.orderId === selectedOrderId;

    await reloadOrderItemsQuantity(true, combination.orderId, isSelectedOrder);
  }, [combination.orderId, combination.kitId, updateKitGridWithNoOrder]);

  return {
    grid,
    kitGridRef,
    gridToPrices,
    gridButtonStatus,
    handleOnChangeGrid,
    handleRemoveKitFromOrder,
  };
};

export default useKitDetailsCombination;
